import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, ChefHat, Plus, Trash2, RotateCcw, Calculator } from 'lucide-react';

interface IngredientRow {
  id: number;
  nombre: string;
  cantidad: string;
  unidad: string;
  precioPorUnidad: string;
}

const UNITS = ['g', 'kg', 'ml', 'L', 'ud'];

let nextId = 4;

const faqs = [
  {
    q: '¿Qué es el food cost y por qué es importante?',
    a: 'El food cost es el porcentaje que representa el coste de los ingredientes sobre el precio de venta de un plato. Es la métrica más importante en hostelería porque determina la rentabilidad real de cada plato. Un food cost del 30% significa que de cada euro que ingresa el restaurante, 30 céntimos se destinan a materia prima.',
  },
  {
    q: '¿Cuál es el food cost ideal para un restaurante?',
    a: 'El food cost óptimo está entre el 25% y el 35% del precio de venta. Por debajo del 30% se considera excelente (verde), entre 30-35% es aceptable (amarillo) y por encima del 35% es una señal de alarma (rojo). Estos rangos pueden variar según el tipo de restaurante y la gama del establecimiento.',
  },
  {
    q: '¿Cómo se calcula el food cost de un plato?',
    a: 'La fórmula es sencilla: Food Cost (%) = (Coste total de ingredientes / Precio de venta) x 100. Por ejemplo, si un plato cuesta 4€ en ingredientes y lo vendes a 14€, el food cost es (4/14) x 100 = 28,6%. Nuestra calculadora de arriba te lo calcula automáticamente.',
  },
  {
    q: '¿Cada cuánto debo revisar el food cost de mi carta?',
    a: 'Deberías revisar el food cost cada vez que cambien los precios de los ingredientes, idealmente de forma semanal. Los precios de productos frescos pueden variar hasta un 20% en una semana. Con software como PLEKO, esto se actualiza automáticamente cada vez que registras una nueva factura.',
  },
  {
    q: '¿Qué hago si mi food cost es demasiado alto?',
    a: 'Hay varias estrategias: 1) Negociar mejores precios con proveedores, 2) Buscar ingredientes alternativos de coste similar, 3) Ajustar las porciones (sin perder calidad percibida), 4) Subir ligeramente el precio del plato, 5) Reducir mermas con mejor manipulación y conservación. PLEKO incluye un optimizador con IA que te sugiere mejoras automáticamente.',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

function createEmptyRow(): IngredientRow {
  return { id: nextId++, nombre: '', cantidad: '', unidad: 'g', precioPorUnidad: '' };
}

export default function CalculadoraFoodCostPage() {
  const [ingredients, setIngredients] = useState<IngredientRow[]>([
    { id: 1, nombre: 'Solomillo de ternera', cantidad: '200', unidad: 'g', precioPorUnidad: '24.90' },
    { id: 2, nombre: 'Patata', cantidad: '150', unidad: 'g', precioPorUnidad: '1.20' },
    { id: 3, nombre: 'Aceite de oliva', cantidad: '15', unidad: 'ml', precioPorUnidad: '8.50' },
  ]);
  const [precioVenta, setPrecioVenta] = useState('18');

  const updateIngredient = (id: number, field: keyof IngredientRow, value: string) => {
    setIngredients((prev) => prev.map((ing) => (ing.id === id ? { ...ing, [field]: value } : ing)));
  };

  const addRow = () => {
    setIngredients((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: number) => {
    if (ingredients.length <= 1) return;
    setIngredients((prev) => prev.filter((ing) => ing.id !== id));
  };

  const reset = () => {
    nextId = 4;
    setIngredients([
      { id: 1, nombre: '', cantidad: '', unidad: 'g', precioPorUnidad: '' },
      { id: 2, nombre: '', cantidad: '', unidad: 'g', precioPorUnidad: '' },
      { id: 3, nombre: '', cantidad: '', unidad: 'g', precioPorUnidad: '' },
    ]);
    setPrecioVenta('');
  };

  // Calculate cost per ingredient row
  const getRowCost = (ing: IngredientRow): number => {
    const cantidad = parseFloat(ing.cantidad.replace(',', '.'));
    const precio = parseFloat(ing.precioPorUnidad.replace(',', '.'));
    if (isNaN(cantidad) || isNaN(precio)) return 0;

    // Convert to base unit cost: precio is per unit (kg, L, ud)
    // cantidad is in the selected unit
    let factor = 1;
    if (ing.unidad === 'g') factor = 0.001; // g -> kg
    if (ing.unidad === 'ml') factor = 0.001; // ml -> L
    if (ing.unidad === 'kg' || ing.unidad === 'L' || ing.unidad === 'ud') factor = 1;

    return cantidad * factor * precio;
  };

  const costeTotal = ingredients.reduce((sum, ing) => sum + getRowCost(ing), 0);
  const pvpSugerido30 = costeTotal > 0 ? costeTotal / 0.3 : 0;
  const pvp = parseFloat(precioVenta.replace(',', '.'));
  const foodCostPct = !isNaN(pvp) && pvp > 0 ? (costeTotal / pvp) * 100 : null;
  const beneficioBruto = !isNaN(pvp) && pvp > 0 ? pvp - costeTotal : null;

  const getTrafficLight = (pct: number) => {
    if (pct < 30) return { color: 'green', label: 'Excelente', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' };
    if (pct <= 35) return { color: 'yellow', label: 'Aceptable', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' };
    return { color: 'red', label: 'Revisar', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' };
  };

  const getPriceLabel = (unit: string) => {
    if (unit === 'g' || unit === 'kg') return '€/kg';
    if (unit === 'ml' || unit === 'L') return '€/L';
    return '€/ud';
  };

  return (
    <>
      <Helmet>
        <title>Calculadora de Food Cost Gratis [2026] | Calcula el Coste de tus Platos | PLEKO</title>
        <meta
          name="description"
          content="Calcula el food cost de tus platos gratis con nuestra calculadora online. Introduce ingredientes, cantidades y precios para conocer tu coste y margen real."
        />
        <meta name="keywords" content="calculadora food cost, calcular food cost, food cost calculator, calculadora coste plato, food cost restaurante" />
        <link rel="canonical" href="https://pleko.app/calculadora-food-cost" />
        <meta property="og:title" content="Calculadora de Food Cost Gratis [2026] | Calcula el Coste de tus Platos | PLEKO" />
        <meta property="og:description" content="Calcula el food cost de tus platos gratis con nuestra calculadora online. Introduce ingredientes, cantidades y precios para conocer tu coste y margen real." />
        <meta property="og:url" content="https://pleko.app/calculadora-food-cost" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Calculadora de Food Cost Gratis [2026] | Calcula el Coste de tus Platos | PLEKO" />
        <meta name="twitter:description" content="Calcula el food cost de tus platos gratis con nuestra calculadora online. Introduce ingredientes, cantidades y precios para conocer tu coste y margen real." />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Calculator className="w-4 h-4" />
                Herramienta gratuita
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Calculadora de <span className="text-blue-500">Food Cost</span> Gratis
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Introduce los ingredientes de tu plato, sus cantidades y precios. Calcula al instante el coste real,
                el food cost y el margen de beneficio.
              </p>
            </div>
          </div>
        </section>

        {/* Calculator */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="border border-slate-200 rounded-2xl p-6 sm:p-8 mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Ingredientes del plato</h2>

            {/* Header row - hidden on mobile */}
            <div className="hidden sm:grid sm:grid-cols-[1fr_100px_80px_100px_40px] gap-3 mb-3">
              <span className="text-sm font-medium text-slate-500">Ingrediente</span>
              <span className="text-sm font-medium text-slate-500">Cantidad</span>
              <span className="text-sm font-medium text-slate-500">Unidad</span>
              <span className="text-sm font-medium text-slate-500">Precio</span>
              <span></span>
            </div>

            {/* Ingredient rows */}
            <div className="space-y-3">
              {ingredients.map((ing) => (
                <div key={ing.id} className="grid grid-cols-1 sm:grid-cols-[1fr_100px_80px_100px_40px] gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Nombre del ingrediente"
                    value={ing.nombre}
                    onChange={(e) => updateIngredient(ing.id, 'nombre', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="Cantidad"
                    value={ing.cantidad}
                    onChange={(e) => updateIngredient(ing.id, 'cantidad', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <select
                    value={ing.unidad}
                    onChange={(e) => updateIngredient(ing.id, 'unidad', e.target.value)}
                    className="w-full px-2 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={getPriceLabel(ing.unidad)}
                      value={ing.precioPorUnidad}
                      onChange={(e) => updateIngredient(ing.id, 'precioPorUnidad', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => removeRow(ing.id)}
                    className="p-2 text-slate-400 hover:text-red-500 transition-colors justify-self-center"
                    title="Eliminar ingrediente"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={addRow}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Añadir ingrediente
              </button>
              <button
                onClick={reset}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Limpiar
              </button>
            </div>

            {/* Separator */}
            <div className="border-t border-slate-200 mt-6 pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Precio de venta del plato (PVP)</label>
                  <div className="relative w-full sm:w-48">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={precioVenta}
                      onChange={(e) => setPrecioVenta(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Coste total ingredientes</p>
                  <p className="text-2xl font-bold text-slate-900">{costeTotal.toFixed(2)}€</p>
                </div>
              </div>
            </div>

            {/* Results */}
            {foodCostPct !== null && (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className={`${getTrafficLight(foodCostPct).bg} border ${getTrafficLight(foodCostPct).border} rounded-xl p-4 text-center`}>
                  <p className="text-sm text-slate-600 mb-1">Food Cost</p>
                  <p className={`text-3xl font-bold ${getTrafficLight(foodCostPct).text}`}>
                    {foodCostPct.toFixed(1)}%
                  </p>
                  <p className={`text-sm font-medium ${getTrafficLight(foodCostPct).text} mt-1`}>
                    {getTrafficLight(foodCostPct).label}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-sm text-slate-600 mb-1">Beneficio bruto</p>
                  <p className="text-3xl font-bold text-blue-600">{beneficioBruto!.toFixed(2)}€</p>
                  <p className="text-sm text-slate-500 mt-1">por plato vendido</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <p className="text-sm text-slate-600 mb-1">Coste vs PVP</p>
                  <p className="text-3xl font-bold text-slate-800">{costeTotal.toFixed(2)}€ / {pvp.toFixed(2)}€</p>
                  <p className="text-sm text-slate-500 mt-1">ingredientes / venta</p>
                </div>
              </div>
            )}

            {/* PVP suggestion for 30% food cost */}
            {costeTotal > 0 && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-slate-700">
                  <strong>PVP sugerido para un food cost del 30%:</strong>{' '}
                  <span className="text-blue-600 font-bold text-lg">{pvpSugerido30.toFixed(2)} €</span>
                  {foodCostPct !== null && foodCostPct > 30 && (
                    <span className="text-slate-500 ml-2">
                      (actualmente {foodCostPct.toFixed(1)}% — sube el PVP o reduce costes)
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-sm text-slate-600 mb-2">
                Para automatizar esto con todos tus platos, prueba PLEKO gratis
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm"
              >
                Probar PLEKO gratis — 14 dias sin tarjeta
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* Traffic light explanation */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">¿Cómo interpretar tu food cost?</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>semáforo de food cost</strong> te indica de un vistazo si el precio de tu plato es rentable. Estos son los rangos estándar en hostelería:
            </p>
            <div className="space-y-3 mb-10">
              {[
                { label: 'Verde — Excelente', range: 'Food cost < 30%', desc: 'Margen óptimo. El plato es rentable sin sacrificar calidad.', bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500' },
                { label: 'Amarillo — Aceptable', range: 'Food cost 30–35%', desc: 'Margen ajustado. Revisar posibles alternativas de ingredientes o ajuste de precio.', bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500' },
                { label: 'Rojo — Revisar urgente', range: 'Food cost > 35%', desc: 'Margen insuficiente. El plato puede estar generando pérdidas al contabilizar costes indirectos.', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500' },
              ].map(({ label, range, desc, bg, border, dot }) => (
                <div key={label} className={`flex gap-4 p-4 ${bg} rounded-xl border ${border}`}>
                  <div className={`w-3 h-3 rounded-full ${dot} flex-shrink-0 mt-1.5`} />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">{label} <span className="font-normal text-slate-500">({range})</span></p>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-3xl font-bold text-slate-900 mb-6">Automatiza esto con PLEKO</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Esta calculadora es útil para cálculos puntuales, pero imagina tener <strong>todos los platos de tu carta calculados automáticamente</strong>,
              con precios que se actualizan solos cada vez que registras una factura de proveedor.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3">Calculadora manual</h3>
                <ul className="space-y-2 text-sm text-slate-600">
                  {[
                    'Un plato a la vez',
                    'Precios se quedan obsoletos',
                    'Sin historial de cambios',
                    'No detecta subidas de precio',
                    'Errores de cálculo frecuentes',
                  ].map((i) => <li key={i} className="flex gap-2"><span className="text-red-400">✗</span>{i}</li>)}
                </ul>
              </div>
              <div className="border border-blue-200 bg-blue-50 rounded-xl p-5">
                <h3 className="font-bold text-blue-700 mb-3">PLEKO (automático)</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  {[
                    'Toda tu carta calculada en tiempo real',
                    'Precios actualizados con OCR de facturas',
                    'Historial completo de costes',
                    'Alertas cuando sube el food cost',
                    'Optimizador con IA incluido',
                  ].map((i) => <li key={i} className="flex gap-2"><span className="text-green-500">✓</span>{i}</li>)}
                </ul>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre el food cost</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-center text-white">
            <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Deja de calcular a mano. Automatiza tu food cost.</h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula el food cost de todos tus platos en tiempo real, escanea facturas con OCR y te avisa cuando el margen se reduce.
              Prueba gratis 14 días, sin tarjeta de crédito.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
